import { useState } from 'react';
import { useMutation } from 'react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { groupsApi } from '../../../services/groups-api-client';
import {
  claimEditCapabilities,
  editClaimNote,
  editNoteClosesSheet,
  editNoteRequiresRefetch,
  firstNameOf,
  partySizeOptions,
  peopleBesides,
  removeConfirmCopy,
  type ClaimEditContext,
  type ClaimEditNote,
} from '../../../lib/dinnerClaimEdit';
import { formatInZone } from '../../../lib/tripTime';
import MoveClaimPicker from './MoveClaimPicker';
import type { DinnerClaimResult, GroupDinnerClaim, GroupDinnerHold } from '../../../types/groups';

/** Which of the three writes the sheet is waiting on, for the spinner. */
type Step = 'home' | 'rename' | 'move' | 'remove';

/**
 * Everything a host can do to one seat, from a phone, in a restaurant.
 *
 * A bottom sheet rather than a centred Dialog — the shape
 * `ReservationOccupantsDialog` uses — because that one is a desk screen and this
 * one is not. The host using this is standing up, holding the phone in one hand,
 * and every control has to land in the bottom third where a thumb reaches.
 *
 * Two rules the layout enforces rather than suggests:
 *
 *  - STEPS REPLACE THE BODY, they never nest. A second layer on a phone covers
 *    the first, and the host loses the name they were working on.
 *  - The destructive action is last, behind its own step, with its own buttons.
 *    It has to be impossible to confuse with the hold delete, which is still a
 *    native browser prompt — and on iOS that renders with generic OK/Cancel no
 *    matter what its text says, so wording alone could never have been the
 *    difference.
 *
 * Nothing here is optimistic about numbers. There is no client-side cache write
 * of any kind: recomputing `claimedSpots` here would be a second copy of the
 * capacity rule, which is exactly what `dinnerClaim.ts`, `DinnerGuestPicker` and
 * `occupants.ts` each refuse in writing. The chip keeps its old number for the
 * ~300ms until the refetch lands, and the row it belongs to is dimmed while it
 * does. It IS optimistic about intent: the sheet stays open and shows the verb
 * in progress, so a host knows the tap registered.
 */
export default function EditClaimSheet({
  open,
  groupId,
  hold,
  holds,
  claim,
  displayName,
  timeZone,
  onClose,
  onChanged,
  onNotice,
  onOpenGuests,
  onOpenConflict,
}: {
  open: boolean;
  groupId: string;
  /** The table this seat is on now. */
  hold: GroupDinnerHold;
  /** Every non-cancelled hold, for the move destinations. */
  holds: GroupDinnerHold[];
  claim: GroupDinnerClaim;
  displayName: string;
  timeZone: string;
  onClose: () => void;
  /** The table changed: re-read the holds. One invalidate covers a move — both tables live in one key. */
  onChanged: () => void;
  /** A note that outlives the sheet, because the sheet is about to close on it. */
  onNotice: (note: ClaimEditNote) => void;
  onOpenGuests: () => void;
  /** Hop to this guest's other seat on `holdId`. The parent resolves which claim that is. */
  onOpenConflict: (holdId: string, from: GroupDinnerClaim) => void;
}) {
  const [step, setStep] = useState<Step>('home');
  const [note, setNote] = useState<ClaimEditNote | null>(null);
  const [renameTo, setRenameTo] = useState(displayName);

  const firstName = firstNameOf(displayName);
  const capabilities = claimEditCapabilities(claim, displayName);
  const sizes = partySizeOptions(hold, claim);
  // Seats, not rows — a claim is one booking with a party size, so counting
  // rows tells the host "the other 1 person stays" when four of them do.
  const otherCount = peopleBesides(hold, claim.id);
  const confirm = removeConfirmCopy(claim, hold, otherCount, displayName);

  /** The copy needs the name as it stood BEFORE the write; that is how a rename is detected. */
  const context = (destinationVenue?: string): ClaimEditContext => ({
    name: displayName,
    firstName,
    sourceVenue: hold.venueName,
    destinationVenue: destinationVenue ?? null,
    previousPartySize: claim.partySize,
    hasSeatingChart: hold.hasSeatingChart,
  });

  /*
    One place where every outcome lands, so the refetch rule and the close rule
    are stated once. A note the sheet is closing on has to be handed upward or
    the host never reads the confirmation of what they just did.
  */
  const settle = (outcome: unknown, ctx: ClaimEditContext) => {
    const result = editClaimNote(outcome, ctx);
    if (editNoteRequiresRefetch(result)) onChanged();
    if (editNoteClosesSheet(result)) {
      onNotice(result);
      onClose();
      return;
    }
    /*
      A refusal is answered where it happened. The step is deliberately NOT
      reset: a `full` on a move leaves the host on the picker with the other
      tables still in front of them, and a blocked rename leaves them on the
      field they typed into. The alert renders at the top of the body on every
      step, so it is read either way — and a toast would scroll away.
    */
    setNote(result);
  };

  const releaseMutation = useMutation<DinnerClaimResult, unknown, void>(
    () => groupsApi.releaseDinnerClaim(groupId, hold.id, claim.id, {}),
    {
      onSuccess: (result) => settle(result, context()),
      onError: (error) => settle(error, context()),
    },
  );

  const updateMutation = useMutation<DinnerClaimResult, unknown, { guestName?: string; partySize?: number }>(
    // Only what is changing goes on the wire: the server reads an absent field
    // as "leave it alone" and an explicit null as a 400.
    (body) => groupsApi.updateDinnerClaim(groupId, hold.id, claim.id, body),
    {
      onSuccess: (result) => settle(result, context()),
      onError: (error) => settle(error, context()),
    },
  );

  const moveMutation = useMutation<DinnerClaimResult, unknown, GroupDinnerHold>(
    (target) => groupsApi.moveDinnerClaim(groupId, hold.id, claim.id, { toHoldId: target.id }),
    {
      onSuccess: (result, target) => settle(result, context(target.venueName)),
      onError: (error, target) => settle(error, context(target.venueName)),
    },
  );

  const busy = releaseMutation.isLoading || updateMutation.isLoading || moveMutation.isLoading;
  // Which control shows the spinner. Read off the mutation's own variables so
  // there is no second copy of "what is in flight" to fall out of step.
  const sizeInFlight = updateMutation.isLoading ? updateMutation.variables?.partySize : undefined;
  const renameInFlight = updateMutation.isLoading && updateMutation.variables?.guestName !== undefined;

  const runAction = (action: NonNullable<ClaimEditNote['action']>) => {
    if (action.kind === 'open-guests') return onOpenGuests();
    if (action.kind === 'reload') {
      onChanged();
      setNote(null);
      return;
    }
    if (action.holdId) onOpenConflict(action.holdId, claim);
  };

  const goHome = () => {
    setStep('home');
    setNote(null);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={() => { if (!busy) onClose(); }}
      PaperProps={{
        sx: {
          borderRadius: '16px 16px 0 0',
          // The home indicator sits over the bottom 34px of an iPhone, which is
          // exactly where the last control in a bottom sheet lands.
          pb: 'env(safe-area-inset-bottom)',
          maxHeight: '85vh',
        },
      }}
    >
      <Box sx={{ p: 2, overflowY: 'auto' }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
          {step === 'home' ? null : (
            <IconButton size="small" aria-label="Back" disabled={busy} onClick={goHome} sx={{ mt: 0.25 }}>
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
          <Box sx={{ minWidth: 0, flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={800} noWrap>{displayName}</Typography>
            <Typography variant="caption" color="text.secondary">
              {hold.venueName} · {formatInZone(hold.reservedAt, hold.timeZone || timeZone)}
            </Typography>
          </Box>
          <Chip
            size="small"
            sx={{ flexShrink: 0 }}
            label={claim.partySize > 1 ? `party of ${claim.partySize}` : '1 spot'}
          />
        </Stack>

        {/* Refusals render here, in the sheet, not as a toast that scrolls away. */}
        {note ? (
          <Alert
            severity={note.severity}
            sx={{ mb: 2 }}
            action={
              note.action ? (
                <Button color="inherit" size="small" disabled={busy} onClick={() => runAction(note.action!)}>
                  {note.action.label}
                </Button>
              ) : undefined
            }
          >
            {note.message}
          </Alert>
        ) : null}

        {step === 'home' ? (
          <Stack spacing={2} divider={<Divider flexItem />}>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Party size</Typography>
              {/*
                No Save button: a tap IS the intent. The stepper's ceiling is the
                server's `maxPartySize` for this table, rendered rather than
                derived — the cap is relative to the table, so a client cannot
                compute it, and it is a render hint either way. The server still
                answers `party_too_large`.
              */}
              <ToggleButtonGroup
                exclusive
                size="small"
                value={claim.partySize}
                onChange={(_, size) => {
                  if (typeof size === 'number' && size !== claim.partySize) {
                    setNote(null);
                    updateMutation.mutate({ partySize: size });
                  }
                }}
              >
                {sizes.map((option) => (
                  <ToggleButton key={option.size} value={option.size} disabled={busy || option.disabled}>
                    {sizeInFlight === option.size ? <CircularProgress size={16} /> : option.size}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
              {/* The reason inline, so a greyed-out 3 is not a mystery. */}
              {sizes.some((option) => option.disabled) ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {sizes.find((option) => option.disabled)?.reason}
                </Typography>
              ) : null}
            </Box>

            <Box>
              <Button fullWidth disabled={busy} onClick={() => { setNote(null); setStep('move'); }}>
                Move to another table
              </Button>
            </Box>

            <Box>
              {capabilities.canRename ? (
                <Button
                  fullWidth
                  disabled={busy}
                  onClick={() => { setNote(null); setRenameTo(displayName); setStep('rename'); }}
                >
                  Rename this guest
                </Button>
              ) : (
                /*
                  Shown disabled with the reason, never hidden. A missing control
                  reads as a bug, and a host hunting for it taps the controls
                  either side of where it should have been — one of which takes a
                  seat away.
                */
                <Stack spacing={0.5}>
                  <Button fullWidth disabled>Rename this guest</Button>
                  <Typography variant="caption" color="text.secondary">
                    {capabilities.renameBlockedReason}{' '}
                    <Button size="small" onClick={onOpenGuests} sx={{ p: 0, minWidth: 0 }}>
                      Open the Guests tab
                    </Button>
                  </Typography>
                </Stack>
              )}
            </Box>

            <Box>
              <Button
                fullWidth
                color="error"
                disabled={busy}
                onClick={() => { setNote(null); setStep('remove'); }}
              >
                {`Take ${firstName} off this table`}
              </Button>
            </Box>
          </Stack>
        ) : null}

        {step === 'rename' ? (
          <Stack spacing={1.5}>
            {/*
              The field is FIRST in the scrollable body and autofocuses only
              here. A sheet that raised the keyboard on open would put it over
              the remove button of a host who came to take somebody off.
            */}
            <TextField
              autoFocus
              fullWidth
              size="small"
              label="Name on this seat"
              value={renameTo}
              onChange={(e) => setRenameTo(e.target.value)}
              // 120 is the plus-one name bound, reused rather than re-minted: a
              // host-typed name lands on the same place card as a guest-typed one.
              inputProps={{ maxLength: 120 }}
              helperText="This is the name the restaurant reads off the list."
            />
            <Button
              variant="contained"
              disabled={busy || !renameTo.trim() || renameTo.trim() === displayName}
              onClick={() => { setNote(null); updateMutation.mutate({ guestName: renameTo.trim() }); }}
              startIcon={renameInFlight ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {renameInFlight ? 'Saving…' : 'Save name'}
            </Button>
          </Stack>
        ) : null}

        {step === 'move' ? (
          <Stack spacing={1.5}>
            <Typography variant="subtitle2" fontWeight={700}>
              {`Move ${firstName} to`}
            </Typography>
            {moveMutation.isLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">Moving…</Typography>
              </Stack>
            ) : null}
            <MoveClaimPicker
              holds={holds}
              fromHold={hold}
              partySize={claim.partySize}
              timeZone={timeZone}
              disabled={busy}
              onPick={(target) => { setNote(null); moveMutation.mutate(target); }}
            />
          </Stack>
        ) : null}

        {step === 'remove' ? (
          /*
            The per-claim destructive confirm, and it differs from the hold
            delete by widget, by verb, by noun and by button label — not by one
            sentence inside two identical grey boxes. The hold delete is a native
            browser prompt, which on iOS renders with generic OK/Cancel, so it
            can never look like this no matter what its text says.
          */
          <Stack spacing={1.5}>
            <Typography variant="subtitle1" fontWeight={700}>{confirm.title}</Typography>
            <Typography variant="body2" color="text.secondary">{confirm.body}</Typography>
            {/*
              The destructive button is NOT in the band the previous step's
              destructive button occupied.

              This is a bottom-anchored drawer, so every step is measured upward
              from the same edge, and the step swap is an instant conditional
              render with no disabled window. "Take Ana off this table" is
              full-width in the bottom band on the home step; if the confirm put
              its own red button there too, the second half of a double tap —
              the reflex this codebase already documents for guests on hotel
              wifi — would commit a release with no undo anywhere in the app.

              So the order is inverted here relative to the home step: confirm
              on top, and the bottom band, where the finger already is, is
              "Keep their seat". Landing on it costs nothing.
            */}
            <Stack spacing={1}>
              <Button
                variant="contained"
                color="error"
                fullWidth
                disabled={busy}
                onClick={() => { setNote(null); releaseMutation.mutate(); }}
                startIcon={releaseMutation.isLoading ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {releaseMutation.isLoading ? 'Taking off…' : confirm.confirmLabel}
              </Button>
              <Button fullWidth variant="outlined" disabled={busy} onClick={goHome}>
                {confirm.cancelLabel}
              </Button>
            </Stack>
          </Stack>
        ) : null}

        {/*
          Always. A host taking somebody off a table on the night has to know
          whether that guest is about to get a message about it, and the answer
          on every one of these routes is no — no email, no SMS, no workflow.
        */}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          This sends nothing to the guest.
        </Typography>
      </Box>
    </Drawer>
  );
}
